import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import './AccessoriesTryOn.css';
import Layout from '../components/Layout';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccessoriesTryOn = () => {
  const navigate = useNavigate();
  const { user, sessionToken, refreshUser } = useAuth();
  
  // Mode: 'accessories_only' or 'combined'
  const [mode, setMode] = useState('accessories_only');
  
  // Images
  const [personImage, setPersonImage] = useState(null);
  const [outfitResultImage, setOutfitResultImage] = useState(null);
  
  // Accessories
  const [accessories, setAccessories] = useState([]);
  const [selectedAccessories, setSelectedAccessories] = useState([]);
  const [resultImages, setResultImages] = useState([]);
  
  // Categories
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedSubcategory, setSelectedSubcategory] = useState('all');
  
  // Search
  const [searchQuery, setSearchQuery] = useState('');
  const [gender, setGender] = useState('all');
  
  // UI State
  const [loading, setLoading] = useState(false);
  const [loadingMessage, setLoadingMessage] = useState('');
  const [error, setError] = useState(null);
  
  const personInputRef = useRef(null);
  const outfitInputRef = useRef(null);

  // Load categories on mount
  useEffect(() => {
    loadCategories();
    loadAccessories();
  }, []);

  const loadCategories = async () => {
    try {
      const response = await axios.get(`${API}/accessories/categories`);
      setCategories(response.data.categories || []);
    } catch (err) {
      console.error('Error loading categories:', err);
    }
  };

  const loadAccessories = async () => {
    try {
      setLoading(true);
      setLoadingMessage('Loading accessories...');
      
      const params = {
        query: searchQuery || 'fashion accessories',
        limit: 30
      };
      
      if (selectedCategory !== 'all') {
        params.category = selectedCategory;
      }
      if (selectedSubcategory !== 'all') {
        params.subcategory = selectedSubcategory;
      }
      if (gender !== 'all') {
        params.gender = gender;
      }
      
      const response = await axios.get(`${API}/accessories/search`, { params });
      
      setAccessories(response.data.accessories || []);
      setLoadingMessage('');
    } catch (err) {
      console.error('Error loading accessories:', err);
      setError('Failed to load accessories');
    } finally {
      setLoading(false);
    }
  };

  const handlePersonImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPersonImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleOutfitImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setOutfitResultImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const toggleAccessory = (accessory) => {
    const isSelected = selectedAccessories.find(a => a.id === accessory.id);
    
    if (isSelected) {
      setSelectedAccessories(selectedAccessories.filter(a => a.id !== accessory.id));
    } else {
      setSelectedAccessories([...selectedAccessories, accessory]);
    }
  };

  const handleGenerate = async () => {
    if (!sessionToken) {
      setError('Please login to use try-on feature');
      return;
    }

    if (selectedAccessories.length === 0) {
      setError('Please select at least one accessory');
      return;
    }

    if (mode === 'accessories_only' && !personImage) {
      setError('Please upload your photo');
      return;
    }

    if (mode === 'combined' && !outfitResultImage) {
      setError('Please upload outfit result image');
      return;
    }

    // Calculate credits needed
    const creditsNeeded = selectedAccessories.length * 2;
    if (user && user.image_credits < creditsNeeded) {
      setError(`Insufficient credits. You need ${creditsNeeded} credits but have ${user.image_credits}`);
      return;
    }

    try {
      setLoading(true);
      setLoadingMessage(`Generating try-on for ${selectedAccessories.length} accessory(ies)...`);
      setError(null);

      const formData = new FormData();
      formData.append('mode', mode);
      formData.append('accessories_data', JSON.stringify(selectedAccessories));
      
      if (mode === 'accessories_only') {
        formData.append('person_image', personImage);
      } else {
        formData.append('outfit_result_image', outfitResultImage);
        formData.append('person_image', personImage); // May still be needed
      }

      const response = await axios.post(
        `${API}/accessories/tryon`,
        formData,
        {
          headers: {
            'Authorization': `Bearer ${sessionToken}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        setResultImages(response.data.results);
        setLoadingMessage('');
        
        // Refresh user credits
        await refreshUser();
        
        // Show success message
        alert(`Successfully generated ${response.data.results.length} try-on images! Credits used: ${response.data.credits_used}`);
      }

    } catch (err) {
      console.error('Try-on error:', err);
      
      if (err.response?.status === 402) {
        setError(err.response.data.detail || 'Insufficient credits');
      } else if (err.response?.status === 401) {
        setError('Session expired. Please login again');
      } else {
        setError(err.response?.data?.detail || 'Failed to generate try-on');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    loadAccessories();
  };

  const getSubcategories = () => {
    if (selectedCategory === 'all') return [];
    const category = categories.find(c => c.id === selectedCategory);
    return category?.subcategories || [];
  };

  return (
    <Layout>
      <div className="accessories-tryon-page">
        <div className="page-header">
          <h1>✨ Accessories Virtual Try-On</h1>
          <p>Try watches, jewelry, hats, bags, and more!</p>
          {user && (
            <div className="credits-badge">
              Credits: {user.image_credits}
            </div>
          )}
        </div>

        {/* Mode Selection */}
        <div className="mode-selector">
          <button
            className={`mode-btn ${mode === 'accessories_only' ? 'active' : ''}`}
            onClick={() => setMode('accessories_only')}
          >
            👤 Accessories Only (2 credits/accessory)
          </button>
          <button
            className={`mode-btn ${mode === 'combined' ? 'active' : ''}`}
            onClick={() => setMode('combined')}
          >
            👔 Outfit + Accessories (3 credits total)
          </button>
        </div>

        {/* Image Upload Section */}
        <div className="upload-section">
          {mode === 'accessories_only' ? (
            <div className="upload-box">
              <h3>Upload Your Photo</h3>
              <input
                type="file"
                ref={personInputRef}
                accept="image/*"
                onChange={handlePersonImageUpload}
                style={{ display: 'none' }}
              />
              <button onClick={() => personInputRef.current.click()}>
                📷 Choose Photo
              </button>
              {personImage && (
                <div className="preview-image">
                  <img src={personImage} alt="Your photo" />
                </div>
              )}
            </div>
          ) : (
            <div className="upload-box">
              <h3>Upload Outfit Result Image</h3>
              <p className="hint">Upload the result from your outfit try-on</p>
              <input
                type="file"
                ref={outfitInputRef}
                accept="image/*"
                onChange={handleOutfitImageUpload}
                style={{ display: 'none' }}
              />
              <button onClick={() => outfitInputRef.current.click()}>
                📷 Choose Outfit Result
              </button>
              {outfitResultImage && (
                <div className="preview-image">
                  <img src={outfitResultImage} alt="Outfit result" />
                </div>
              )}
            </div>
          )}
        </div>

        {/* Search and Filters */}
        <div className="search-section">
          <div className="search-bar">
            <input
              type="text"
              placeholder="Search accessories... (e.g., gold necklace, leather watch)"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
            />
            <button onClick={handleSearch}>🔍 Search</button>
          </div>

          <div className="filters">
            <select
              value={selectedCategory}
              onChange={(e) => {
                setSelectedCategory(e.target.value);
                setSelectedSubcategory('all');
              }}
            >
              <option value="all">All Categories</option>
              {categories.map(cat => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>

            {getSubcategories().length > 0 && (
              <select
                value={selectedSubcategory}
                onChange={(e) => setSelectedSubcategory(e.target.value)}
              >
                <option value="all">All Subcategories</option>
                {getSubcategories().map(sub => (
                  <option key={sub} value={sub}>{sub}</option>
                ))}
              </select>
            )}

            <select value={gender} onChange={(e) => setGender(e.target.value)}>
              <option value="all">All Genders</option>
              <option value="men">Men</option>
              <option value="women">Women</option>
              <option value="unisex">Unisex</option>
            </select>

            <button onClick={loadAccessories} className="apply-filters-btn">
              Apply Filters
            </button>
          </div>
        </div>

        {/* Selected Accessories */}
        {selectedAccessories.length > 0 && (
          <div className="selected-section">
            <h3>Selected Accessories ({selectedAccessories.length})</h3>
            <div className="selected-grid">
              {selectedAccessories.map(acc => (
                <div key={acc.id} className="selected-item">
                  <img src={acc.image_url} alt={acc.name} />
                  <p>{acc.name}</p>
                  <button onClick={() => toggleAccessory(acc)}>❌</button>
                </div>
              ))}
            </div>
            <div className="credits-info">
              Total Credits Needed: {selectedAccessories.length * 2}
            </div>
          </div>
        )}

        {/* Accessories Grid */}
        <div className="accessories-grid">
          {loading && !accessories.length ? (
            <div className="loading">
              <div className="spinner"></div>
              <p>{loadingMessage}</p>
            </div>
          ) : accessories.length === 0 ? (
            <div className="empty-state">
              <p>No accessories found. Try a different search!</p>
            </div>
          ) : (
            accessories.map(accessory => {
              const isSelected = selectedAccessories.find(a => a.id === accessory.id);
              return (
                <div
                  key={accessory.id}
                  className={`accessory-card ${isSelected ? 'selected' : ''}`}
                  onClick={() => toggleAccessory(accessory)}
                >
                  <div className="accessory-image">
                    <img src={accessory.image_url} alt={accessory.name} />
                    {isSelected && <div className="selected-badge">✓</div>}
                  </div>
                  <div className="accessory-info">
                    <h4>{accessory.name}</h4>
                    <p className="brand">{accessory.brand}</p>
                    <p className="category">{accessory.category} • {accessory.subcategory}</p>
                    <p className="price">${accessory.price}</p>
                  </div>
                </div>
              );
            })
          )}
        </div>

        {/* Generate Button */}
        {selectedAccessories.length > 0 && (mode === 'accessories_only' ? personImage : outfitResultImage) && (
          <div className="generate-section">
            <button
              className="generate-btn"
              onClick={handleGenerate}
              disabled={loading}
            >
              {loading ? 'Generating...' : `✨ Generate Try-On (${selectedAccessories.length * 2} credits)`}
            </button>
          </div>
        )}

        {/* Error Display */}
        {error && (
          <div className="error-message">
            ⚠️ {error}
          </div>
        )}

        {/* Results Section */}
        {resultImages.length > 0 && (
          <div className="results-section">
            <h2>Your Try-On Results</h2>
            <div className="results-grid">
              {resultImages.map((result, idx) => (
                <div key={idx} className="result-card">
                  <div className="result-image">
                    <img src={result.result_image} alt="Try-on result" />
                  </div>
                  <div className="result-info">
                    <h4>{result.accessory.name}</h4>
                    <p>{result.accessory.brand}</p>
                    <p className="price">${result.accessory.price}</p>
                  </div>
                  <div className="result-actions">
                    <button onClick={() => {
                      const link = document.createElement('a');
                      link.href = result.result_image;
                      link.download = `${result.accessory.name}-tryon.jpg`;
                      link.click();
                    }}>
                      📥 Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default AccessoriesTryOn;
